/**
 * Room schedule evaluation, shared by the server (admission gate, cleanup) and
 * the client (schedule display, meeting timing) so both agree on every window.
 *
 * The schedule's `[startTime, endTime]` is the first occurrence; its duration
 * is `endTime - startTime`. A `recurrence` repeats that window indefinitely.
 * With a `timeZone`, occurrences keep the first start's wall-clock time in that
 * zone, so a weekly 10:00 stays at 10:00 across daylight saving changes.
 * Without one (schedules saved before zones existed) daily/weekly advance by a
 * fixed number of milliseconds and monthly by calendar months in UTC.
 * Monthly always preserves the day-of-month, clamped to the target month's
 * length (Jan 31 + 1 month => Feb 28/29).
 */
const DAY_MS = 24 * 60 * 60 * 1000;
/** Average Gregorian month, only used to estimate an occurrence index. */
const MONTH_MS = 30.436875 * DAY_MS;
/** One-time schedules expire strictly after their inclusive end instant. */
export function isScheduleExpired(schedule, now) {
    return schedule !== undefined && !schedule.recurrence &&
        now > schedule.endTime;
}
/** Whether `now` (epoch ms) falls inside an active occurrence of `schedule`. */
export function isWithinSchedule(schedule, now) {
    const last = lastOccurrence(schedule, now);
    return last !== null && now <= last.end;
}
/**
 * Classify a schedule relative to `now` and return the relevant occurrence
 * window: the active one when in progress, the next one when upcoming, or the
 * (single, past) window when a one-off has ended.
 */
export function scheduleStatus(schedule, now) {
    const { startTime, endTime, recurrence } = schedule;
    const duration = endTime - startTime;
    // Before the first occurrence: the first window is what's coming up.
    if (now < startTime) {
        return { state: "upcoming", start: startTime, end: endTime };
    }
    if (!recurrence) {
        return now <= endTime
            ? { state: "in-progress", start: startTime, end: endTime }
            : { state: "ended", start: startTime, end: endTime };
    }
    const k = occurrenceIndexAtOrBefore(schedule, now);
    const occStart = occurrenceStart(schedule, k);
    if (now <= occStart + duration) {
        return { state: "in-progress", start: occStart, end: occStart + duration };
    }
    // Past the current window — the next occurrence is upcoming (recurs forever).
    const next = occurrenceStart(schedule, k + 1);
    return { state: "upcoming", start: next, end: next + duration };
}
/**
 * The latest occurrence that started at or before `now` (its end may already
 * be in the past), or null before the first occurrence. Unlike
 * {@link scheduleStatus} this never rolls over to the next occurrence, which
 * is what overrun detection needs.
 */
export function lastOccurrence(schedule, now) {
    if (now < schedule.startTime)
        return null;
    const start = occurrenceStart(schedule, occurrenceIndexAtOrBefore(schedule, now));
    return { start, end: start + (schedule.endTime - schedule.startTime) };
}
/** Beyond this, an overrun is treated as a stale session, not a long meeting. */
export const OVERRUN_MAX_MS = 4 * 60 * 60 * 1000;
/**
 * Classify a session (started at `sessionStart`, epoch ms) against `schedule`.
 * Overrun only counts for a session that was there before the scheduled end,
 * so someone joining a daily room hours after its window sees the next
 * occurrence instead of a "+7h" timer.
 */
export function meetingTiming(schedule, sessionStart, now) {
    if (!schedule)
        return { phase: "unscheduled", window: null, overrunMs: 0 };
    const last = lastOccurrence(schedule, now);
    if (last && now <= last.end) {
        return { phase: "running", window: last, overrunMs: 0 };
    }
    if (last &&
        sessionStart !== null &&
        sessionStart <= last.end &&
        now - last.end <= OVERRUN_MAX_MS) {
        return { phase: "overrun", window: last, overrunMs: now - last.end };
    }
    const status = scheduleStatus(schedule, now);
    return {
        phase: status.state === "upcoming" ? "upcoming" : "ended",
        window: { start: status.start, end: status.end },
        overrunMs: 0,
    };
}
/**
 * Index of the latest occurrence starting at or before `now`. Estimates it
 * from the nominal period, then corrects the estimate, which daylight saving
 * shifts and uneven month lengths put off by at most one. Caller guarantees
 * `now >= startTime`.
 */
function occurrenceIndexAtOrBefore(schedule, now) {
    const { startTime, recurrence } = schedule;
    if (!recurrence)
        return 0;
    let k = Math.max(0, Math.floor((now - startTime) / nominalPeriodMs(recurrence)));
    while (occurrenceStart(schedule, k + 1) <= now)
        k++;
    while (k > 0 && occurrenceStart(schedule, k) > now)
        k--;
    return k;
}
function nominalPeriodMs({ frequency, interval }) {
    switch (frequency) {
        case "daily":
            return interval * DAY_MS;
        case "weekly":
            return interval * 7 * DAY_MS;
        case "monthly":
            return interval * MONTH_MS;
    }
}
/** Start instant of occurrence `k` (0 is the first). */
function occurrenceStart(schedule, k) {
    const { startTime, recurrence, timeZone } = schedule;
    if (!recurrence || k === 0)
        return startTime;
    const steps = k * recurrence.interval;
    if (recurrence.frequency !== "monthly" && !timeZone) {
        return startTime + steps * nominalPeriodMs({ ...recurrence, interval: 1 });
    }
    const zone = timeZone ?? "UTC";
    const wall = wallClock(startTime, zone);
    if (recurrence.frequency === "monthly") {
        const monthIndex = wall.month + steps;
        const year = wall.year + Math.floor(monthIndex / 12);
        const month = ((monthIndex % 12) + 12) % 12;
        const daysInMonth = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
        return wallToInstant({ ...wall, year, month, day: Math.min(wall.day, daysInMonth) }, zone);
    }
    const days = steps * (recurrence.frequency === "weekly" ? 7 : 1);
    const date = new Date(Date.UTC(wall.year, wall.month, wall.day + days));
    return wallToInstant({
        ...wall,
        year: date.getUTCFullYear(),
        month: date.getUTCMonth(),
        day: date.getUTCDate(),
    }, zone);
}
// Creating an `Intl.DateTimeFormat` costs far more than formatting with one,
// and the meeting clock evaluates schedules every second.
const formatters = new Map();
function zoneFormatter(zone) {
    let formatter = formatters.get(zone);
    if (!formatter) {
        formatter = new Intl.DateTimeFormat("en-US", {
            timeZone: zone,
            hourCycle: "h23",
            year: "numeric",
            month: "numeric",
            day: "numeric",
            hour: "numeric",
            minute: "numeric",
            second: "numeric",
        });
        formatters.set(zone, formatter);
    }
    return formatter;
}
function wallClock(epochMs, zone) {
    const parts = {};
    for (const part of zoneFormatter(zone).formatToParts(epochMs)) {
        if (part.type !== "literal")
            parts[part.type] = Number(part.value);
    }
    return {
        year: parts.year,
        month: parts.month - 1,
        day: parts.day,
        hour: parts.hour,
        minute: parts.minute,
        second: parts.second,
        millisecond: ((epochMs % 1000) + 1000) % 1000,
    };
}
/** The zone's UTC offset at `epochMs`, in ms (positive east of Greenwich). */
function offsetAt(epochMs, zone) {
    const wall = wallClock(epochMs, zone);
    return wallAsUtc(wall) - epochMs;
}
function wallAsUtc(w) {
    return Date.UTC(w.year, w.month, w.day, w.hour, w.minute, w.second, w.millisecond);
}
/**
 * The instant at which the zone's clock shows `wall`. A time repeated when the
 * clocks go back resolves to its first (earlier) instant; a time skipped when
 * they go forward moves forward by the skipped length (02:30 → 03:30), as
 * Temporal's "compatible" disambiguation does.
 */
function wallToInstant(wall, zone) {
    const asUtc = wallAsUtc(wall);
    // Offsets a day either side bracket any single transition near this time.
    const before = offsetAt(asUtc - DAY_MS, zone);
    const after = offsetAt(asUtc + DAY_MS, zone);
    const early = asUtc - before;
    const late = asUtc - after;
    const earlyValid = offsetAt(early, zone) === before;
    const lateValid = offsetAt(late, zone) === after;
    if (earlyValid && lateValid)
        return Math.min(early, late);
    if (earlyValid)
        return early;
    if (lateValid)
        return late;
    // In the gap: read the wall time with the offset in force before it.
    return early;
}
